import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, Button, IconButton, TextField, Paper,
  Alert, MenuItem, Select, FormControl, InputLabel, OutlinedInput, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { areas, DATA_URL, agencies } from '../config';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {formatUTCPeriod} from "./EventDetail";

const ModalStyles = {
  overlay: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', position: 'fixed',
    top: 0, left: 0, width: '100%', height: '100%', zIndex: 1000,
  },
  modal: {
    position: 'relative', width: '90%', maxHeight: '92%',
    maxWidth: '800px', overflow: 'auto', borderRadius: '0px',
    padding: '20px', backgroundColor: '#f8f8f8'
  },
  sectionHeader: {
    backgroundColor: '#1a475f',
    color: 'white',
    padding: '10px',
    marginTop: '10px',
  },
  sectionContent: {
    marginBottom: '12px',
    border: '1px solid #d9d9d9',
    borderRadius: '2px',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderTop: 'none',
    padding: '10px',
    backgroundColor: '#f8f8f8'
  }
};

const militarySubTypes = ["Exercise", "Real Operations", "Special"];
const specialSubTypes = ["Military", "Miscellaneous"];

const nmSubTypes = ["NM"];
const vSOSubTypes = agencies
  .filter(ag => ag.value !== 'Civil' && ag.value !== 'Military')
  .map(ag => ag.value);


const makeUtcDate = (timestamp) => {
    if (!timestamp) return null;
    const d = new Date(timestamp);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), 0, 0));
};

const EventForm = ({user, open, onClose, initialData, onSaved }) => {
  const [formData, setFormData] = useState({
    type: '',subType: '',name: '',status: 'Planned',estimatedPeriod: '',locations: [],
    description: '',attachments: [],measures: '',activities: '',
    impact: 'no',impactDesc: '',originatorComments: '',nmComments: '',
    organisationType: 'NETWORK_MANAGER',organisationName: 'NM',imageUrl: '',attachmentImageUrl: ''
  });
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(initialData?.estimatedPeriod?.[0] ? makeUtcDate(initialData.estimatedPeriod[0]) : null);
  const [endDate, setEndDate] = useState(initialData?.estimatedPeriod?.[1] ? makeUtcDate(initialData.estimatedPeriod[1]) : null);  
  const [isNetworkManager, setIsNetworkManager] = useState(false);
  const [isCreator, setIsCreator] = useState(true);

  useEffect(() => {
    if (open) {
      setIsNetworkManager(false);
      setIsCreator(true);
    }
  }, [initialData]);  

  useEffect(() => {
    if (!initialData) return;
  
    const isCreator = initialData.creator?.toString() === user.cid.toString();
    
    const userHousekeeperLocations = user.housekeeper?.split(',') || [];
    const isHousekeeper = initialData.locations?.some(loc => userHousekeeperLocations.includes(loc));
    setIsNetworkManager(isHousekeeper && !isCreator ? true : isCreator && isHousekeeper ? true : false);
    setIsCreator(isCreator);

  }, [initialData, user]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        type: initialData.type || '',
        subType: initialData.subtype || '',
        name: initialData.name || '',
        status: initialData.status || 'Planned',
        estimatedPeriod: initialData.estimatedPeriod || '',
        locations: initialData.locations || [],
        description: initialData.description || '',
        attachments: initialData.attachments || [],
        measures: initialData.measures || '',
        activities: initialData.activities || '',
        impact: initialData.impact || 'no',
        impactDesc: initialData.impactdesc || '',
        originatorComments: initialData.originatorcomments || '',
        nmComments: initialData.nmcomments || '',
        organisationType: initialData.organisationtype || 'NETWORK_MANAGER',
        organisationName: initialData.organisationname || 'NM',
        imageUrl: initialData.imageurl || '',
        attachmentImageUrl: initialData.attachmentimageurl || ''
      });
  
      // Convert estimatedPeriod timestamps to local Date objects for pickers
      if (initialData.estimatedPeriod?.length === 2) {
        setStartDate(new Date(Number(initialData.estimatedPeriod[0])));
        setEndDate(new Date(Number(initialData.estimatedPeriod[1])));        
      } else {
        setStartDate(null);
        setEndDate(null);
      }
    } else {
      // Clear form for new event
      setFormData({
        type: '',
        subType: '',
        name: '',
        status: 'Planned',
        estimatedPeriod: '',
        locations: [],
        description: '',
        attachments: [],
        measures: '',
        activities: '',
        impact: 'no',
        impactDesc: '',
        originatorComments: '',
        nmComments: '',
        organisationType: 'NETWORK_MANAGER',
        organisationName: 'NM',
        imageUrl: '',
        attachmentImageUrl: ''
      });
      setStartDate(null);
      setEndDate(null);
    }
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAttachmentChange = (index, field, value) => {
    const newAttachments = [...formData.attachments];
    newAttachments[index][field] = value;
    setFormData(prev => ({ ...prev, attachments: newAttachments }));
  };

  const addAttachment = () => {
    setFormData(prev => ({ ...prev, attachments: [...prev.attachments, { name: '', type: '', url: '' }] }));
  };

  const removeAttachment = (index) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, attachments: newAttachments }));
  };

  const handleSubmit = async () => {
    // Validate form
    const requiredFields = {
        name: "Name",
        type: "Type",
        subType: "Subtype",
        organisationName: "Organisation Name",
        locations: "Locations",
        description: "Description"
    };
      
    const missingFields = Object.entries(requiredFields)
        .filter(([key]) => !formData[key])
        .map(([_, label]) => label);
      
    if (missingFields.length > 0) {
        setError(`Missing required fields: ${missingFields.join(', ')}`);
        return;
    }
    if (!startDate || !endDate || (endDate - startDate) < 3600000) {
        setError('Event must be at least 1 hour long');
        return;
    }
    setError('');

    // Prepare payload
    const payload = {
        ...formData,
        estimatedPeriod: [startDate.getTime(), endDate.getTime()],
        ...(initialData?.id ? {} : { creator: user.cid })
    };

    try {
        let res;
        if (initialData?.id) {
            // Edit existing event
            res = await fetch(`${DATA_URL}editEvent/${initialData.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload),
            });
        } else {
            // Create new event
            res = await fetch(`${DATA_URL}createEvent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload),
            });
        }

        if (!res.ok) {
            const errData = await res.json();
            throw new Error(errData.error || 'Failed to save');
        }

        if (onSaved) onSaved();
        onClose();
    } catch (err) {
        setError(err.message);
    }
  };

  const subtypeOptions = formData.type === 'MILITARY' ? militarySubTypes : formData.type === 'SPECIAL' ? specialSubTypes : [];
  const organisationOptions = formData.organisationType === 'NETWORK_MANAGER' ? nmSubTypes : formData.organisationType === 'VSOA' ? vSOSubTypes : ['CIVIL', 'MILITARY'];

  const insertMarkdown = (syntaxStart, syntaxEnd = syntaxStart) => {
    const textarea = document.getElementById("description-field");
    if (!textarea) return;
  
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.description.substring(start, end);
  
    const newText =
      formData.description.substring(0, start) +
      syntaxStart + selectedText + syntaxEnd +
      formData.description.substring(end);
  
    handleChange("description", newText);
  
    // Restore focus and selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + syntaxStart.length, end + syntaxStart.length);
    }, 0);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box style={ModalStyles.overlay}>
        <Box style={ModalStyles.modal}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {initialData ? 'Edit Network Event' : 'Create Network Event'}
            </Typography>
            <IconButton onClick={onClose}><CloseIcon /></IconButton>
          </Box>

          {isCreator && (
          <>

          <Box style={ModalStyles.sectionHeader} className='section-header-bg'>
            <div>Basic</div>
          </Box>

          <Box style={ModalStyles.sectionContent} className={'sectionContent'}>

            <TextField
                size="small"
                fullWidth
                sx={{ mt: 2 }}
                label="Event Name"
                value={formData.name}
                error={/[^a-zA-Z0-9 ]/.test(formData.name)}
                helperText={/[^a-zA-Z0-9 ]/.test(formData.name) ? "Only letters, numbers and spaces allowed" : ""}
                onChange={e => handleChange("name", e.target.value)}
            />

            {/* Type & Subtype */}
            <Box display="flex" gap={2} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                <InputLabel size="small">Type</InputLabel>
                <Select
                    size="small"
                    value={formData.type}
                    label="Type"
                    onChange={e => handleChange('type', e.target.value)}
                >
                    <MenuItem value="MILITARY">MILITARY</MenuItem>
                    <MenuItem value="SPECIAL">SPECIAL</MenuItem>
                </Select>
                </FormControl>
                <FormControl fullWidth>
                <InputLabel size="small">Sub Type</InputLabel>
                <Select
                    size="small"
                    value={formData.subType}
                    label="Sub Type"
                    onChange={e => handleChange('subType', e.target.value)}
                >
                    {subtypeOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
                </FormControl>
            </Box>

            {/* Organisation */}
            <Box display="flex" gap={2} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                <InputLabel size="small">Organisation Type</InputLabel>
                <Select
                    size="small"
                    value={formData.organisationType}
                    label="Organisation Type"
                    onChange={e => handleChange('organisationType', e.target.value)}
                >
                    <MenuItem value="NETWORK_MANAGER">NETWORK_MANAGER</MenuItem>
                    <MenuItem value="VSOA">Virtual Special Operations Association</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                </Select>
                </FormControl>
                <FormControl fullWidth>
                <InputLabel size="small">Organisation Name</InputLabel>
                <Select
                    size="small"
                    value={formData.organisationName}
                    label="Organisation Name"
                    onChange={e => handleChange('organisationName', e.target.value)}
                >
                    {organisationOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
                </FormControl>
            </Box>

            <Box sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Event Image URL"
                    value={formData.imageUrl}
                    onChange={e => handleChange('imageUrl', e.target.value)}
                    helperText="Must be a valid URL ending in .jpg, .jpeg or .png"
                />

                {formData.imageUrl && /\.(jpg|jpeg|png)$/i.test(formData.imageUrl) ? (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                        src={formData.imageUrl}
                        alt="Event preview"
                        style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #ccc' }}
                        onError={() => handleChange('imageUrl', 'no input')}
                    />
                    </Box>
                ) : formData.imageUrl === 'no input' ? (
                    <Typography sx={{ mt: 2, color: 'gray', fontStyle: 'italic' }}>
                        No input
                    </Typography>
                ) : null}
            </Box>

          </Box>

          <Box style={ModalStyles.sectionHeader} className='section-header-bg'>
            <div>Locations and Time</div>
          </Box>
          
          <Box style={ModalStyles.sectionContent} className={'sectionContent'}>
            {/* Locations */}
            <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel size="small">Locations</InputLabel>
                <Select
                multiple
                size="small"
                value={formData.locations}
                onChange={e => handleChange('locations', e.target.value)}
                input={<OutlinedInput label="Locations" />}
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => {
                        const area = areas.find(a => a.value === val);
                        return <Chip key={val} label={area?.label || val} />;
                    })}
                    </Box>
                )}
                >
                {areas.map((a) => (
                    <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
                </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box display="flex" justifyContent="space-between" gap={1}>
                    {[
                    { label: 'Start Date', value: startDate, onChange: setStartDate, type: 'date' },
                    { label: 'Start Time UTC', value: startDate, onChange: setStartDate, type: 'time' },
                    { label: 'End Date', value: endDate, onChange: setEndDate, type: 'date' },
                    { label: 'End Time UTC', value: endDate, onChange: setEndDate, type: 'time' },
                    ].map(({ label, type, value, onChange }, idx) => {
                    const Picker = type === 'date' ? DatePicker : TimePicker;
                    const className = type === 'date' ? 'calendar-icon' : 'timepicker-icon';

                    return (
                        <Box key={idx} flex={1}>
                        <p className="modal-sub-title">{label}</p>
                        <Picker
                            value={value ? makeUtcDate(value) : null}
                            onChange={(date) => onChange(date ? makeUtcDate(date) : null)}
                            {...(type === 'time' ? { ampm: false } : { format: 'dd/MM/yyyy' })}
                            slots={{ textField: TextField }}
                            slotProps={{
                            textField: {
                                size: 'small',
                                className: `text-fields ${className}`,
                                InputProps: {
                                sx: {
                                    '& .MuiSvgIcon-root': { color: 'transparent' },
                                },
                                },
                            },
                            }}
                        />
                        </Box>
                    );
                    })}
                </Box>
            </LocalizationProvider>

          </Box>

          {/* Description */}
         <Box style={ModalStyles.sectionHeader} className='section-header-bg'>
            <div>Description</div>
          </Box>

          <Box style={ModalStyles.sectionContent} className={'sectionContent'}>
            {/* Formatting Toolbar */}
            <Box display="flex" gap={1}>
              <IconButton size="small" onClick={() => insertMarkdown("**")}>
                <FormatBoldIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => insertMarkdown("*")}>
                <FormatItalicIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => insertMarkdown("__")}>
                <FormatUnderlinedIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* TextArea */}
            <TextField
              id="description-field"
              fullWidth
              multiline
              rows={7}
              sx={{ mt: 1, mb: 1 }}
              label="Description"
              value={formData.description}
              onChange={e => handleChange("description", e.target.value)}
              error={formData.description.length > 8000}
              helperText={
                formData.description.length > 8000
                  ? `Maximum 8000 characters allowed (${formData.description.length}/8000)`
                  : `${formData.description.length}/8000`
              }
            />
   
            {/* Attachments */}
            {formData.attachments.map((att, i) => (
            <Box key={i} display="flex" gap={1} sx={{ mt: 1, alignItems: 'center' }}>
                <TextField size="small" label="Reference Name" value={att.name} onChange={e => handleAttachmentChange(i, 'name', e.target.value)} sx={{ flex: 2 }} />
                <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Type</InputLabel>
                <Select multiple value={att.type || []} onChange={e => handleAttachmentChange(i, 'type', e.target.value)} input={<OutlinedInput label="Reference Type" />} renderValue={selected => selected.join(', ')}>
                    {['NOTAM','AIP', 'SUP', 'Pilot Briefing', 'ATS Briefing', 'Chart', 'Other'].map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
                </FormControl>
                <TextField size="small" label="URL" value={att.url} onChange={e => handleAttachmentChange(i, 'url', e.target.value)} sx={{ flex: 2 }} />
                <IconButton size="small" color="error" onClick={() => removeAttachment(i)}><DeleteIcon/></IconButton>
            </Box>
            ))}
            <Button sx={{ mt: 1 }} size="small" onClick={addAttachment}>Add Reference Publication</Button>

            <Box sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Attachment Image URL"
                    value={formData.attachmentImageUrl}
                    onChange={e => handleChange('attachmentImageUrl', e.target.value)}
                    helperText="Must be a valid URL ending in .jpg, .jpeg or .png"
                />

                {formData.attachmentImageUrl && /\.(jpg|jpeg|png)$/i.test(formData.attachmentImageUrl) ? (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                        src={formData.attachmentImageUrl}
                        alt="Event preview"
                        style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #ccc' }}
                        onError={() => handleChange('imageUrl', 'no input')}
                    />
                    </Box>
                ) : formData.imageUrl === 'no input' ? (
                    <Typography sx={{ mt: 2, color: 'gray', fontStyle: 'italic' }}>
                        No input
                    </Typography>
                ) : null}
            </Box>

          </Box>

          {/* Measures, Activities, Impacts */}
          <Box style={ModalStyles.sectionHeader} className='section-header-bg'>
            <div>Activity</div>
          </Box>

          <Box style={ModalStyles.sectionContent} className={'sectionContent'}>
            <TextField fullWidth multiline rows={4} sx={{ mt: 2 }} label="Activities"
                value={formData.activities}
                onChange={e => handleChange("activities", e.target.value)}
                error={formData.activities.length > 4000}
                helperText={
                formData.activities.length > 4000
                    ? `Maximum 4000 characters allowed (${formData.activities.length}/4000)`
                    : `${formData.activities.length}/4000`
                }
            />

            <TextField fullWidth multiline rows={4} sx={{ mt: 2 }} label="Number of Flights & FL Range"
                value={formData.measures}
                onChange={e => handleChange("measures", e.target.value)}
                error={formData.measures.length > 4000}
                helperText={
                formData.measures.length > 4000
                    ? `Maximum 4000 characters allowed (${formData.measures.length}/4000)`
                    : `${formData.measures.length}/4000`
                }
            />
          </Box>


          {/* Comments */}
          <Box style={ModalStyles.sectionHeader} className='section-header-bg'>
            <div>Comments</div>
          </Box>

          <Box style={ModalStyles.sectionContent} className={'sectionContent'}>
            <TextField fullWidth multiline rows={2} sx={{ mt: 2 }} label="Organisation Comments"
                value={formData.originatorComments}
                onChange={e => handleChange("originatorComments", e.target.value)}
                error={formData.originatorComments.length > 2000}
                helperText={
                formData.originatorComments.length > 2000
                    ? `Maximum 2000 characters allowed (${formData.originatorComments.length}/2000)`
                    : `${formData.originatorComments.length}/2000`
                }
            />
          </Box>
          </>
          )}

          {isNetworkManager && (
            <>
            {!isCreator && (
                <>
                <Box style={ModalStyles.sectionHeader} className='section-header-bg'>
                    <div>Event Details</div>
                </Box>
                <Box style={ModalStyles.sectionContent} className={'sectionContent'}>
                    <Typography variant='h6'>{formData.name}</Typography>
                    <Typography>{formData.type} {formData.subType}</Typography>
                    <Typography mt={1}><b>Period:</b> {formatUTCPeriod(formData.estimatedPeriod)}</Typography>
                    <Typography style={{ whiteSpace: 'pre-line' }}>
                        <b>Creator:</b> {initialData?.creator} {formData.organisationName}
                    </Typography>

                    <Typography mt={2}><b>Description</b></Typography>
                    <Box sx={{}}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {formData.description}
                        </ReactMarkdown>
                    </Box>

                    <Typography mt={2}><b>Flights</b></Typography>
                    <Typography style={{ whiteSpace: 'pre-line' }}>{formData.measures}</Typography>

                    <Typography mt={2}><b>Activities</b></Typography>
                    <Typography style={{ whiteSpace: 'pre-line' }}>{formData.activities}</Typography>

                    <Typography mt={2}><b>More Details</b></Typography>
                    <Typography style={{ whiteSpace: 'pre-line' }}>
                        <a href={`https://lara.lusep.fi/#/events/${initialData?.id}`} target="_blank" rel="noopener noreferrer">https://lara.lusep.fi/#/events/{initialData?.id}</a>
                    </Typography>
                </Box>
                </>
            )}

            <Box style={ModalStyles.sectionHeader} className='section-header-bg'>
                <div>Network Manager</div>
            </Box>

            <Box style={ModalStyles.sectionContent} className={'sectionContent'}>

                <Box display="flex" gap={2} sx={{ mt: 2 }}>
                    <FormControl fullWidth>
                    <InputLabel size="small">Network Impact</InputLabel>
                    <Select
                        size="small"
                        value={formData.impact}
                        label="Network Impact"
                        onChange={e => handleChange('impact', e.target.value)}
                    >
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                    </Select>
                    </FormControl>
                </Box>

                <TextField fullWidth multiline rows={4} sx={{ mt: 2 }} label="Impacts"
                    value={formData.impactDesc}
                    onChange={e => handleChange("impactDesc", e.target.value)}
                    error={formData.impactDesc.length > 4000}
                    helperText={
                        formData.impactDesc.length > 4000
                        ? `Maximum 4000 characters allowed (${formData.impactDesc.length}/4000)`
                        : `${formData.impactDesc.length}/4000`
                    }
                />
                <TextField fullWidth multiline rows={2} sx={{ mt: 2 }} label="Network Manager Comments"
                    value={formData.nmComments}
                    onChange={e => handleChange("nmComments", e.target.value)}
                    error={formData.nmComments.length > 2000}
                    helperText={
                        formData.nmComments.length > 2000
                        ? `Maximum 2000 characters allowed (${formData.nmComments.length}/2000)`
                        : `${formData.nmComments.length}/2000`
                    }
                />

            </Box>
            </>
          )}

          <Box mb={2} mt={2}>
              <Alert severity="info">Cluster Housekeepers can approve Planned Events and add details for Network Impacts. The event is approved automatically if any associated airspace reservation is activated by AMC.</Alert>
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>

          <Button variant="contained" sx={{ mt: 1, borderRadius: 0 }} onClick={handleSubmit}>Save Event</Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EventForm;
